import type { User } from '@shared/types/user';
import type { Domain } from '@/types/navigation';

/**
 * Determine a user's domain (the single dashboard/UI they belong to) from
 * their company_id and permissions.
 *
 * Rules (PRBA — Permission-Only RBAC):
 *  - Each user belongs to exactly one domain
 *  - Domain is determined by dashboard permission AND company context
 *  - No role-based fallback
 *  - Auditors with a company_id are INTERNAL; without, they are EXTERNAL
 *
 * Safe fallback is 'compliance' when the user object is missing or malformed.
 */
export function resolveUserDomain(user: Pick<User, 'company_id' | 'permissions'> | null | undefined): Domain {
  if (!user || !Array.isArray(user.permissions)) return 'compliance';

  const permissions = user.permissions;
  const companyId = user.company_id;

  if (!companyId && permissions.includes('admin.dashboard.view')) return 'admin';
  if (!companyId && permissions.includes('partner.dashboard.view')) return 'partner';

  if (companyId) {
    if (permissions.includes('company.dashboard.view')) return 'company';
    if (permissions.includes('audit.dashboard.view')) return 'audit';
    if (permissions.includes('compliance.dashboard.view')) return 'compliance';
  }

  if (!companyId && permissions.includes('audit.dashboard.view')) return 'audit';

  return 'compliance';
}
